import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Platform } from "react-native";
import { AppDispatch, RootState } from "../../store";
import {
  loginStart,
  loginSuccess,
  loginFailure,
  tokenAcquisitionStart,
  tokenAcquisitionSuccess,
  tokenAcquisitionFailure,
  logout,
  logoutError,
} from "../../store/slices/authSlice";
import acquireTokenSilent, { clearCachedToken } from "./acquireTokenSilent";
import { getMsalInstance, clearMsalInstances } from "./msalInstance";

import LHLoader from "../LHLoader";
import MsalProfile from "../MsalProfile";
import AsyncStorage from "@react-native-async-storage/async-storage";

const MOBILE_SCOPE = "e3a34560-5d71-4055-b2d7-6ab366704411/.default";
const MOBILE_PROMPT_SELECT_ACCOUNT = 0;
// Use WHEN_REQUIRED (3) for automatic/fallback token acquisition so the
// Authenticator account picker is NOT forced when a valid broker session
// already exists.  SELECT_ACCOUNT (0) is reserved for explicit user-initiated
// sign-in and the very first login on a shared device (no accounts found).
const MOBILE_PROMPT_WHEN_REQUIRED = 3;
const MOBILE_SELECTED_ACCOUNT_KEY = "msal_mobile_selected_account_id";

const getMobileWebviewParameters = () =>
  Platform.OS === "ios"
    ? {
        ios_prefersEphemeralWebBrowserSession: true,
      }
    : undefined;

// Types
type MsalContextType = {
  signIn: () => void | Promise<void>;
  signOut: () => void | Promise<void>;
  acquireTokenSilent: () => Promise<string | null>;
  account: any;
  accessToken: string | null;
};

// Context
const MsalContext = createContext<MsalContextType | null>(null);
export const useMsal = () => useContext(MsalContext)!;

// Provider Component
export const MsalProvider = ({ children }: { children: React.ReactNode }) => {
  const [account, setAccount] = useState<any>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const msalInstanceRef = useRef<any>(null);

  // Redux hooks
  const loading = useSelector((state: RootState) => state.auth.loading);
  const user = useSelector((state: RootState) => state.auth.user);
  const dispatch = useDispatch<AppDispatch>();

  // Debug: Log both account states
  useEffect(() => {
    console.log("🔍 MsalProvider local account:", account ? "SET" : "NULL");
    console.log("🔍 MsalProvider Redux user:", user ? "SET" : "NULL");
    if (account) console.log("🔍 Local account username:", account.username);
    if (user) console.log("🔍 Redux user username:", user.username);
  }, [account, user]);

  // Watch for auth state changes and update local state accordingly
  useEffect(() => {
    if (user && user.idToken && !account) {
      // If Redux has a user with token but local state doesn't, sync them
      setAccount(user);
      setAccessToken(user.idToken);
    } else if (!user && account) {
      // If Redux lost the user but local state still has it, clear local state
      setAccount(null);
      setAccessToken(null);
    }
  }, [user, account]);

  // Helper function to handle login errors
  const handleAuthError = (error: any, source: string) => {
    console.error(`Auth error (${source}):`, error);
    dispatch(loginFailure(error));
    setIsInitializing(false);
  };

  // Track if we've already initiated a login redirect to prevent multiple redirects
  const hasInitiatedLoginRef = useRef(false);

  const persistSelectedMobileAccount = async (nextAccount: any) => {
    if (Platform.OS === "web") return;
    const identifier = nextAccount?.identifier;
    if (!identifier) return;
    try {
      await AsyncStorage.setItem(MOBILE_SELECTED_ACCOUNT_KEY, identifier);
    } catch (storageError) {
      console.warn("Failed to persist selected mobile account:", storageError);
    }
  };

  const clearSelectedMobileAccount = async () => {
    if (Platform.OS === "web") return;
    try {
      await AsyncStorage.removeItem(MOBILE_SELECTED_ACCOUNT_KEY);
    } catch (storageError) {
      console.warn("Failed to clear selected mobile account:", storageError);
    }
  };
  
  useEffect(() => {
    const init = async () => {
      try {
        console.log("🚀 MSAL Provider initialization started");
        dispatch(loginStart());
        setIsInitializing(true);

        // Use the singleton MSAL instance
        const instance = await getMsalInstance();
        msalInstanceRef.current = instance;
        
        console.log("✅ MSAL instance created successfully");

        if (Platform.OS === "web") {
          console.log("🌐 Web platform detected, starting web auth flow");
          
          // Check if we're in the middle of a redirect
          const urlParams = new URLSearchParams(window.location.search);
          const isRedirectCallback = urlParams.has('code') || urlParams.has('error');
          
          if (isRedirectCallback) {
            console.log("🔍 Redirect callback detected in URL");
          }
          
          // Handle any redirects first - this is critical for web flow
          try {
            console.log("🔄 Handling redirect promise...");
            const response = await instance.handleRedirectPromise();
            if (response) {
              console.log("✅ Successful redirect response received:", response);
              setAccount(response.account);
              setAccessToken(response.accessToken);
              dispatch(loginSuccess(response.account));
              dispatch(tokenAcquisitionSuccess(response.accessToken));
              setIsInitializing(false);
              return; // Important: exit early after successful redirect
            } else {
              console.log("ℹ️ No redirect response - continuing with normal flow");
            }
          } catch (redirectError) {
            console.warn("❌ Error handling redirect:", redirectError);
            // Don't return here, continue with normal flow
          }

          // Look for existing accounts only if no redirect response
          const accounts = instance.getAllAccounts();
          const activeAccount = instance.getActiveAccount();
          const currentUser = activeAccount || (accounts.length > 0 ? accounts[0] : null);

          console.log("🔍 Web platform - accounts found:", accounts.length);
          console.log("🔍 Active account:", activeAccount ? "SET" : "NULL");
          console.log("🔍 Current user:", currentUser ? currentUser.username : "NULL");

          if (currentUser) {
            console.log("🔍 Found existing user account:", currentUser.username);
            setAccount(currentUser);

            // Set active account if not already set
            if (!activeAccount && accounts.length > 0) {
              instance.setActiveAccount(accounts[0]);
            }

            // Try silent token acquisition with better error handling
            try {
              console.log("🔄 Attempting silent token acquisition...");
              const token = await acquireTokenSilent(false); // Don't force refresh initially
              if (token) {
                console.log("✅ Silent token acquisition successful");
                setAccessToken(token);
                dispatch(tokenAcquisitionSuccess(token));
                dispatch(loginSuccess(currentUser));
                setIsInitializing(false);
              } else {
                console.log("⚠️ Silent token acquisition returned null - checking redirect state");
                // Only redirect if we're sure the user needs to authenticate
                // and we're not already in a redirect flow
                const urlParams = new URLSearchParams(window.location.search);
                const isRedirectCallback = urlParams.has('code') || urlParams.has('error');
                
                console.log("🔍 URL params check - isRedirectCallback:", isRedirectCallback);
                console.log("🔍 Current URL:", window.location.href);
                
                if (!isRedirectCallback && !hasInitiatedLoginRef.current) {
                  console.log("🔄 Starting login redirect flow for existing user");
                  try {
                    // Mark that we've initiated a login to prevent multiple redirects
                    hasInitiatedLoginRef.current = true;
                    console.log("⚠️ Login redirect initiated - preventing further redirects");
                    
                    // Try popup first as it's less likely to cause issues
                    console.log("🔄 Trying popup login first");
                    try {
                      const popupResult = await instance.loginPopup({
                        scopes: ["User.Read"],
                        prompt: "select_account",
                      });
                      
                      if (popupResult) {
                        console.log("✅ Popup login successful:", popupResult);
                        setAccount(popupResult.account);
                        setAccessToken(popupResult.accessToken);
                        dispatch(loginSuccess(popupResult.account));
                        dispatch(tokenAcquisitionSuccess(popupResult.accessToken));
                        setIsInitializing(false);
                        return;
                      }
                    } catch (popupError) {
                      console.log("⚠️ Popup login failed, falling back to redirect:", popupError);
                      // Fall back to redirect if popup fails
                      await instance.loginRedirect({
                        scopes: ["User.Read"],
                        prompt: "select_account",
                      });
                    }
                  } catch (redirectError) {
                    console.error("❌ Login redirect failed:", redirectError);
                    handleAuthError(redirectError, "web-existing-user-redirect");
                  }
                } else {
                  console.log("🔄 Already in redirect flow, waiting...");
                  setIsInitializing(false);
                }
              }
            } catch (tokenError) {
              console.error("❌ Token acquisition failed during initialization:", tokenError);
              clearCachedToken();
              
              // Check if we're already in a redirect flow
              const urlParams = new URLSearchParams(window.location.search);
              const isRedirectCallback = urlParams.has('code') || urlParams.has('error');
              
              console.log("🔍 Token error - isRedirectCallback:", isRedirectCallback);
              
              if (!isRedirectCallback && !hasInitiatedLoginRef.current) {
                try {
                  console.log("🔄 Starting login after token error");
                  // Mark that we've initiated a login to prevent multiple redirects
                  hasInitiatedLoginRef.current = true;
                  console.log("⚠️ Login initiated - preventing further redirects");
                  
                  // Try popup first as it's less likely to cause issues
                  console.log("🔄 Trying popup login first");
                  try {
                    const popupResult = await instance.loginPopup({
                      scopes: ["User.Read"],
                      prompt: "select_account",
                    });
                    
                    if (popupResult) {
                      console.log("✅ Popup login successful:", popupResult);
                      setAccount(popupResult.account);
                      setAccessToken(popupResult.accessToken);
                      dispatch(loginSuccess(popupResult.account));
                      dispatch(tokenAcquisitionSuccess(popupResult.accessToken));
                      setIsInitializing(false);
                      return;
                    }
                  } catch (popupError) {
                    console.log("⚠️ Popup login failed, falling back to redirect:", popupError);
                    // Fall back to redirect if popup fails
                    await instance.loginRedirect({
                      scopes: ["User.Read"],
                      prompt: "select_account",
                    });
                  }
                } catch (loginError) {
                  console.error("❌ Login redirect after token error failed:", loginError);
                  handleAuthError(loginError, "web-login-redirect");
                }
              } else {
                console.log("🔄 In redirect flow after token error, waiting...");
                setIsInitializing(false);
              }
            }
          } else {
            console.log("👤 No existing accounts found - need fresh login");
            
            // Check if we're already in a redirect flow before starting a new one
            const urlParams = new URLSearchParams(window.location.search);
            const isRedirectCallback = urlParams.has('code') || urlParams.has('error');
            
            console.log("🔍 No accounts - isRedirectCallback:", isRedirectCallback);
            console.log("🔍 Current URL:", window.location.href);
            
            if (!isRedirectCallback && !hasInitiatedLoginRef.current) {
              console.log("🔄 Starting initial login flow");
              try {
                // Mark that we've initiated a login to prevent multiple redirects
                hasInitiatedLoginRef.current = true;
                console.log("⚠️ Login initiated - preventing further redirects");
                
                // For localhost development, we might need to handle redirect URI issues
                const isLocalhost = window.location.hostname === 'localhost' || 
                                   window.location.hostname === '127.0.0.1';
                
                if (isLocalhost) {
                  console.log("⚠️ Localhost detected - trying popup first as it's more reliable");
                  try {
                    // Try popup first for localhost as it's more reliable
                    const popupResult = await instance.loginPopup({
                      scopes: ["User.Read"],
                      prompt: "select_account",
                    });
                    
                    if (popupResult) {
                      console.log("✅ Popup login successful:", popupResult);
                      setAccount(popupResult.account);
                      setAccessToken(popupResult.accessToken);
                      dispatch(loginSuccess(popupResult.account));
                      dispatch(tokenAcquisitionSuccess(popupResult.accessToken));
                      setIsInitializing(false);
                      return;
                    }
                  } catch (popupError) {
                    console.log("⚠️ Popup login failed, falling back to redirect:", popupError);
                    // Fall back to redirect
                    console.log("💡 If redirect fails, ensure http://localhost:8080/ is added to Azure AD app registration");
                    
                    await instance.loginRedirect({
                      scopes: ["User.Read"],
                      prompt: "select_account",
                    });
                  }
                } else {
                  // For non-localhost, try redirect first
                  await instance.loginRedirect({
                    scopes: ["User.Read"],
                    prompt: "select_account",
                  });
                }
                console.log("🔄 Login redirect initiated successfully");
              } catch (loginError) {
                console.error("❌ Initial login redirect failed:", loginError);
                
                // If redirect fails due to redirect URI issue, try popup as fallback
                if (typeof loginError === 'object' && 
                    loginError !== null && 
                    'message' in loginError && 
                    typeof (loginError as Error).message === 'string' &&
                    ((loginError as Error).message.includes("redirect_uri") || 
                     (loginError as Error).message.includes("AADSTS50011"))) {
                  console.log("🔄 Trying popup as fallback for redirect URI issue");
                  try {
                    const popupResult = await instance.loginPopup({
                      scopes: ["User.Read"],
                      prompt: "select_account",
                    });
                    
                    if (popupResult) {
                      console.log("✅ Popup login successful:", popupResult);
                      setAccount(popupResult.account);
                      setAccessToken(popupResult.accessToken);
                      dispatch(loginSuccess(popupResult.account));
                      dispatch(tokenAcquisitionSuccess(popupResult.accessToken));
                      setIsInitializing(false);
                      return;
                    }
                  } catch (popupError) {
                    console.error("❌ Popup login also failed:", popupError);
                  }
                }
                
                handleAuthError(loginError, "web-initial-login");
              }
            } else {
              console.log("🔄 Already in redirect flow, waiting for completion...");
              setIsInitializing(false);
            }
          }
        } else {
          // Mobile platforms - similar logic with singleton instance
          const accounts = await instance.getAccounts();
          const selectedAccountIdentifier = await AsyncStorage.getItem(MOBILE_SELECTED_ACCOUNT_KEY);
          const preferredAccount = selectedAccountIdentifier
            ? accounts.find((entry: any) => entry.identifier === selectedAccountIdentifier)
            : null;
          const initialAccount = preferredAccount || accounts[0] || null;
          console.log(`Found ${accounts.length} accounts on mobile`);

          if (accounts.length > 0) {
            setAccount(initialAccount);
            console.log("Account:", initialAccount);
            console.log("Account keys:", Object.keys(initialAccount || {}));

            try {
              // Use forceRefresh:false so the in-memory cache is checked first.
              // On shared iPads, forceRefresh:true always contacts the broker
              // (Authenticator) which can open a UI prompt and start the loop.
              const token = await acquireTokenSilent(false, initialAccount?.identifier);
              if (token) {
                setAccessToken(token);
                dispatch(tokenAcquisitionSuccess(token));
                if (initialAccount) {
                  await persistSelectedMobileAccount(initialAccount);
                  dispatch(loginSuccess(initialAccount));
                }
                setIsInitializing(false);
              } else {
                console.log("Silent token acquisition returned null, trying interactive login");
                try {
                  const result = await instance.acquireToken({
                    scopes: [MOBILE_SCOPE],
                    // Accounts already exist — use WHEN_REQUIRED so the broker
                    // only prompts if there is genuinely no active session.
                    // SELECT_ACCOUNT here causes the Authenticator loop on
                    // shared iPads.
                    promptType: MOBILE_PROMPT_WHEN_REQUIRED,
                    webviewParameters: getMobileWebviewParameters(),
                  });

                  setAccount(result.account);
                  setAccessToken(result.accessToken);
                  await persistSelectedMobileAccount(result.account);
                  dispatch(loginSuccess(result.account));
                  dispatch(tokenAcquisitionSuccess(result.accessToken));
                  setIsInitializing(false);
                } catch (acquireError) {
                  handleAuthError(acquireError, "mobile-acquire-token");
                }
              }
            } catch (tokenError) {
              console.error("Token acquisition failed during initialization:", tokenError);
              clearCachedToken();

              try {
                const result = await instance.acquireToken({
                  scopes: [MOBILE_SCOPE],
                  // Same as above — use WHEN_REQUIRED to avoid the loop.
                  promptType: MOBILE_PROMPT_WHEN_REQUIRED,
                  webviewParameters: getMobileWebviewParameters(),
                });

                setAccount(result.account);
                setAccessToken(result.accessToken);
                await persistSelectedMobileAccount(result.account);
                dispatch(loginSuccess(result.account));
                dispatch(tokenAcquisitionSuccess(result.accessToken));
                setIsInitializing(false);
              } catch (acquireError) {
                handleAuthError(acquireError, "mobile-acquire-token-fallback");
              }
            }
          } else {
            console.log("No accounts found on mobile, starting interactive login");
            try {
              const result = await instance.acquireToken({
                scopes: [MOBILE_SCOPE],
                promptType: MOBILE_PROMPT_SELECT_ACCOUNT,
                webviewParameters: getMobileWebviewParameters(),
              });

              setAccount(result.account);
              setAccessToken(result.accessToken);
              await persistSelectedMobileAccount(result.account);
              dispatch(loginSuccess(result.account));
              dispatch(tokenAcquisitionSuccess(result.accessToken));
              setIsInitializing(false);
            } catch (acquireError) {
              handleAuthError(acquireError, "mobile-initial-login");
            }
          }
        }
      } catch (error) {
        handleAuthError(error, "init-general");
      }
    };

    init();
  }, [dispatch]);

  const signOut = async () => {
    if (!msalInstanceRef.current) {
      console.error("MSAL instance not initialized");
      return;
    }

    try {
      clearCachedToken();
      clearMsalInstances(); // Clear singleton instances

      if (Platform.OS === "web") {
        await msalInstanceRef.current.logoutRedirect();
      } else {
        const accounts = await msalInstanceRef.current.getAccounts();
        for (const account of accounts) {
          if (typeof msalInstanceRef.current.signOut === "function") {
            await msalInstanceRef.current.signOut({
              account,
              // On iOS this also clears the browser session; on Android the library
              // treats this the same as removeAccount.
              signoutFromBrowser: true,
              webviewParameters: getMobileWebviewParameters(),
            });
          } else {
            await msalInstanceRef.current.removeAccount(account);
          }
        }
        // Clear AsyncStorage entries for shared-device safety
        try {
          await AsyncStorage.removeItem("selectedLocation");
        } catch (storageError) {
          console.warn("Failed to clear AsyncStorage on logout:", storageError);
        }
        await clearSelectedMobileAccount();
      }

      setAccount(null);
      setAccessToken(null);
      dispatch(logout());
    } catch (error) {
      console.error("Error during sign out:", error);
      dispatch(logoutError(error));
    }
  };

    const signIn = async () => {
    // If we're already in the process of logging in, don't start another attempt
    if (hasInitiatedLoginRef.current) {
      console.log("⚠️ Login already in progress, ignoring duplicate request");
      return;
    }
    
    dispatch(loginStart());
    if (!msalInstanceRef.current) {
      console.error("MSAL instance not initialized");
      dispatch(loginFailure(new Error("MSAL instance not initialized")));
      return;
    }

    try {
      // Mark that we've initiated login to prevent duplicates
      hasInitiatedLoginRef.current = true;
      
      if (Platform.OS === "web") {
        const instance = msalInstanceRef.current;
        const isLocalhost = window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1';
        
        if (isLocalhost) {
          // For localhost, try popup first as it's more reliable
          try {
            console.log("🔄 Trying popup login for localhost");
            const popupResult = await instance.loginPopup({
              // Use API-specific scope for API access
              scopes: [process.env.MSAL_CLIENT_ID + "/.default"],
              prompt: "select_account",
            });
            
            if (popupResult) {
              console.log("✅ Popup login successful:", popupResult);
              setAccount(popupResult.account);
              setAccessToken(popupResult.accessToken);
              dispatch(loginSuccess(popupResult.account));
              dispatch(tokenAcquisitionSuccess(popupResult.accessToken));
              return;
            }
          } catch (popupError) {
            console.log("⚠️ Popup login failed, falling back to redirect:", popupError);
            // Fall back to redirect
            await instance.loginRedirect({
              // Use API-specific scope for API access
              scopes: [process.env.MSAL_CLIENT_ID + "/.default"],
              prompt: "select_account",
            });
          }
        } else {
          // For non-localhost environments, use redirect
          await instance.loginRedirect({
            // Use API-specific scope for API access
            scopes: [process.env.MSAL_CLIENT_ID + "/.default"],
            prompt: "select_account",
          });
        }
      } else {
        // For mobile platforms, we use the default client ID
        // For React Native MSAL, the client ID was already used to initialize the MSAL instance
        // so we don't need to dynamically load it here
        console.log("✅ Starting mobile authentication flow");
        
        const result = await msalInstanceRef.current.acquireToken({
          scopes: [MOBILE_SCOPE],
          promptType: MOBILE_PROMPT_SELECT_ACCOUNT,
          webviewParameters: getMobileWebviewParameters(),
        });

        setAccount(result.account);
        setAccessToken(result.accessToken);
        await persistSelectedMobileAccount(result.account);
        dispatch(loginSuccess(result.account));
        dispatch(tokenAcquisitionSuccess(result.accessToken));
        // AppDynamics user context will be automatically updated via Redux store listener
      }
    } catch (error) {
      console.error("Sign-in error:", error);
      dispatch(loginFailure(error));
    }
  };
  const acquireToken = async (): Promise<string | null> => {
    try {
      dispatch(tokenAcquisitionStart());

      if (!msalInstanceRef.current) {
        console.error("MSAL instance not initialized");
        throw new Error("MSAL instance not initialized");
      }

      const token = await acquireTokenSilent();

      if (token) {
        setAccessToken(token);
        dispatch(tokenAcquisitionSuccess(token));
        return token;
      }

      // Token acquisition failed silently, need interactive login
      console.log(
        "Silent token acquisition failed, attempting interactive login"
      );

      if (Platform.OS === "web") {
        await msalInstanceRef.current.loginRedirect({
          scopes: ["User.Read"],
          prompt: "select_account",
        });
      } else {
        const result = await msalInstanceRef.current.acquireToken({
          scopes: [MOBILE_SCOPE],
          // Use WHEN_REQUIRED — this is an automatic fallback from silent
          // acquisition, not a user-initiated sign-in.  Using SELECT_ACCOUNT
          // here is the primary driver of the Authenticator loop on shared iPads.
          promptType: MOBILE_PROMPT_WHEN_REQUIRED,
          webviewParameters: getMobileWebviewParameters(),
        });

        setAccount(result.account);
        setAccessToken(result.accessToken);
        await persistSelectedMobileAccount(result.account);
        dispatch(loginSuccess(result.account));
        dispatch(tokenAcquisitionSuccess(result.accessToken));
        // AppDynamics user context will be automatically updated via Redux store listener
        return result.accessToken;
      }

      return null;
    } catch (error) {
      console.error("Token acquisition error:", error);
      dispatch(tokenAcquisitionFailure(error));
      return null;
    }
  };

  if (loading && isInitializing) {
    return <LHLoader testID="lh-loader" />;
  }

  return (
    <MsalContext.Provider
      value={{
        signIn,
        signOut,
        acquireTokenSilent: acquireToken,
        account: user || account, // Use Redux user if available, fallback to local account
        accessToken,
      }}
    >
      {(user || account) ? children : <MsalProfile />}
    </MsalContext.Provider>
  );
};
